/* eslint-disable no-nested-ternary */
import 'date-fns';
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Controller } from 'react-hook-form';
import InputMask from 'react-input-mask';
import { TextField as MUITextField, InputAdornment } from '@material-ui/core';
import DateFnsUtils from '@date-io/date-fns';
import brLocale from 'date-fns/locale/pt-BR';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers';
import Icon from './Icon';

const ComponentStyled = styled(MUITextField)`
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    border: none;
    -webkit-text-fill-color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 0 1000px transparent inset;
    transition: background-color 5000s ease-in-out 0s;
    caret-color: ${({ theme }) => theme.colors.white};
  }
  label,
  .MuiInputBase-root {
    box-sizing: border-box;
  }
  label {
    transform: translate(0, 28px) scale(1) !important;
    padding-left: 15px;
    padding-right: 15px;
    &.MuiInputLabel-shrink {
      transform: translate(0, 1.5px) scale(0.75) !important;
    }
  }
  input,
  textarea,
  label,
  .MuiFormHelperText-root,
  .MuiInputAdornment-root {
    color: ${({ theme }) => theme.colors.white} !important;
    background: transparent;
    border: none;
  }

  .MuiInputBase-input {
    padding-top: 10px;
    padding-bottom: 10px;
    padding-left: 15px;
    padding-right: 15px;
  }

  input.Mui-disabled,
  textarea.Mui-disabled,
  label.Mui-disabled,
  .MuiInputAdornment-root.Mui-disabled {
    color: ${({ theme }) => theme.colors.black} !important;
    opacity: 0.4;
  }

  &:focus-within {
    label {
      color: ${({ theme }) => theme.colors.white};
    }
  }
  .MuiInput-underline {
    &::before {
      border-color: ${({ theme }) => theme.colors.white} !important;
    }
    &:hover:not(.Mui-disabled) {
      &::before {
        border-color: ${({ theme }) => theme.colors.white};
        border-width: 1px;
      }
    }
    &.Mui-disabled {
      &::before {
        border-color: ${({ theme }) => theme.colors.black} !important;
        border-bottom-style: solid;
        opacity: 0.3;
      }
    }
    &::after {
      border-color: ${({ theme }) => theme.colors.secondary};
    }
    &.Mui-error {
      &::after {
        border-color: ${({ theme }) => theme.colors.white} !important;
      }
    }
  }
  .MuiFormHelperText-root.Mui-error {
    align-self: flex-start;
    padding: 1px 6px;
    background: ${({ theme }) => theme.colors.danger};
    color: ${({ theme }) => theme.colors.white};
  }

  .MuiIconButton-root {
    color: ${({ theme }) => theme.colors.white};
    font-size: 1rem;
  }

  button {
    outline: none;
  }
`;

export const DateField = ({
  error,
  caption,
  formControl,
  rules,
  name,
  ariaLabel,
  format,
  ...props
}) => {
  const InputProps = {
    error,
    helperText: caption,
    inputProps: {
      type: 'text',
      'aria-label': ariaLabel,
    },
    ...props,
  };

  return (
    // https://material-ui-pickers.dev/api/KeyboardDatePicker
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={brLocale}>
      <Controller
        control={formControl?.control}
        name={name}
        rules={rules}
        defaultValue={null}
        render={({ onChange, onBlur, value }) => {
          return (
            <KeyboardDatePicker
              {...InputProps}
              format={format || 'dd/MM/yyyy'}
              onChange={onChange}
              onBlur={onBlur}
              value={value || null}
              TextFieldComponent={ComponentStyled}
              cancelLabel="Cancelar"
              variant="dialog"
            />
          );
        }}
      />
    </MuiPickersUtilsProvider>
  );
};

export const MaskField = ({
  showMask,
  mask,
  valid,
  error,
  caption,
  inputRef,
  formControl,
  rules,
  label,
  name,
  type = 'text',
  value,
  onChange,
  executeActionOnBlur,
  inputProps,
  ariaLabel,
  ...props
}) => {
  const [focused, setFocused] = useState(false);

  const InputProps = {
    error: focused ? null : error,
    helperText: error && focused ? null : caption,
    type,
    label,
    InputProps: {
      endAdornment: !focused && (error || valid) && (
        <InputAdornment position="end">
          <Icon icon={error ? ['fa', 'times'] : ['fa', 'check']} />
        </InputAdornment>
      ),
      onFocus: () => {
        setFocused(true);
      },
      onBlur: event => {
        setFocused(false);
        if (executeActionOnBlur) {
          executeActionOnBlur(event);
        }
      },
      inputComponent: InputMask,
      inputProps: {
        ...inputProps,
        mask,
        maskChar: showMask ? '_' : '',
        role: 'textbox',
        'aria-label': ariaLabel,
      },
    },
    ...props,
  };

  return (
    <Controller
      {...InputProps}
      as={ComponentStyled}
      control={formControl.control}
      name={name}
      rules={rules}
      defaultValue={value || ''}
    />
  );
};

export const TextField = ({
  valid,
  error,
  caption,
  type,
  onChange,
  value,
  inputRef,
  formControl,
  rules,
  ariaLabel,
  ...props
}) => {
  const [focused, setFocused] = useState(false);
  const firefoxInputNumberCondition = useMemo(
    () => type === 'number' && navigator.userAgent.includes('Firefox'),
    [type],
  );
  const [showPasswordValue, setShowPasswordValue] = useState(false);
  const [inputType, setInputType] = useState(null);

  const firefoxMask = useMemo(
    () => (firefoxInputNumberCondition ? '0000000000000000000000000000' : null),
    [firefoxInputNumberCondition],
  );

  function handleIcon() {
    const showIconValidation =
      !focused && (valid || error) && type !== 'password';
    const showIconPassword = type === 'password';

    function toggleShowPassword() {
      setShowPasswordValue(!showPasswordValue);

      if (showPasswordValue) {
        setInputType('password');
      } else {
        setInputType('text');
      }
    }

    return (
      <InputAdornment position="end">
        {showIconValidation && (
          <Icon icon={error ? ['fa', 'times'] : ['fa', 'check']} />
        )}
        {showIconPassword && (
          <Icon
            style={{
              cursor: 'pointer',
            }}
            onClick={() => toggleShowPassword()}
            icon={showPasswordValue ? ['far', 'eye-slash'] : ['far', 'eye']}
          />
        )}
      </InputAdornment>
    );
  }

  const InputProps = {
    error: focused ? null : error,
    helperText: error && focused ? null : caption,
    type: firefoxInputNumberCondition ? 'text' : inputType || type,
    onChange,
    value,
    InputProps: {
      endAdornment: handleIcon(),
      onFocus: () => {
        setFocused(true);
      },
      onBlur: () => {
        setFocused(false);
      },
    },
    inputProps: {
      'aria-label': ariaLabel,
      role: 'textbox',
    },
    ...props,
  };

  return firefoxInputNumberCondition ? (
    <MaskField {...InputProps} mask={firefoxMask} />
  ) : formControl && !onChange ? (
    <Controller
      {...InputProps}
      as={ComponentStyled}
      control={formControl.control}
      rules={rules}
      value={value}
      onChange={onChange}
      defaultValue={value || ''}
    />
  ) : (
    <ComponentStyled
      {...InputProps}
      value={value}
      onChange={onChange}
      inputRef={
        inputRef || (formControl && onChange && formControl.register(rules))
      }
    />
  );
};

DateField.propTypes = {
  error: PropTypes.bool,
  valid: PropTypes.bool,
  caption: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  rules: PropTypes.object,
  formControl: PropTypes.object,
  defaultValue: PropTypes.string,
  ariaLabel: PropTypes.string,
};

MaskField.propTypes = {
  error: PropTypes.bool,
  valid: PropTypes.bool,
  showMask: PropTypes.bool,
  mask: PropTypes.string,
  caption: PropTypes.string,
  type: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  inputRef: PropTypes.func,
  rules: PropTypes.object,
  formControl: PropTypes.object,
  label: PropTypes.string,
  containerProps: PropTypes.object,
  inputProps: PropTypes.object,
  name: PropTypes.string,
  executeActionOnBlur: PropTypes.func,
  ariaLabel: PropTypes.string,
};

TextField.propTypes = {
  error: PropTypes.bool,
  valid: PropTypes.bool,
  caption: PropTypes.string,
  type: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  inputRef: PropTypes.func,
  rules: PropTypes.object,
  formControl: PropTypes.object,
  ariaLabel: PropTypes.string,
};

export default TextField;
